import React, { useMemo, useState, useRef, useEffect } from 'react'
import { FrameworkType } from 'domains/scope-and-sequence/types'
import { useAllFrameworks } from 'domains/scope-and-sequence/hooks'
import { stringOrNumberSort } from 'components/Helpers'

type FrameworkPickerProps = {
  value: FrameworkType | undefined
  onChange: (framework: FrameworkType | undefined) => void
  filteredJurisdictions?: string[]
}

const ALL_FRAMEWORKS_LABEL = 'All Frameworks'

export const FrameworkPicker: React.FC<FrameworkPickerProps> = ({
  value,
  onChange,
  filteredJurisdictions = [],
}) => {
  const items = useAllFrameworks()
  const [jurisdiction, setJurisdiction] = useState<string | null>(
    ALL_FRAMEWORKS_LABEL,
  )

  // Unique list of jurisdictions, plus "All Frameworks" on top
  const jurisdictions = useMemo(() => {
    const set = new Set<string>()
    items?.forEach((item) => {
      if (!filteredJurisdictions.includes(item.jurisdiction)) {
        set.add(item.jurisdiction)
      }
    })
    const result = Array.from(set).sort(stringOrNumberSort)
    result.unshift(ALL_FRAMEWORKS_LABEL)
    return result
  }, [items, filteredJurisdictions])

  // Items for the selected jurisdiction (or all items)
  const frameworksInJurisdiction = useMemo(() => {
    if (!jurisdiction || jurisdiction === ALL_FRAMEWORKS_LABEL) {
      return items?.filter(
        (item) => !filteredJurisdictions.includes(item.jurisdiction),
      )
    }
    return items?.filter((item) => item.jurisdiction === jurisdiction)
  }, [items, jurisdiction, filteredJurisdictions])

  // When jurisdiction changes, clear the selected framework if it no longer fits
  useEffect(() => {
    if (!value) return

    if (
      jurisdiction &&
      jurisdiction !== ALL_FRAMEWORKS_LABEL &&
      value.jurisdiction !== jurisdiction
    ) {
      onChange(undefined)
    }
    // If jurisdiction is "All Frameworks", any current value is valid
  }, [jurisdiction, value, onChange])

  const handleFrameworkChange = (idStr: string) => {
    if (!idStr) {
      onChange(undefined)
      return
    }
    const id = Number(idStr)
    const selected = frameworksInJurisdiction?.find((f) => f.id === id)
    onChange(selected)
  }

  const OptionLabel = () => {
    const jurisdictionLabel = jurisdiction
      ? `Select a framework in "${jurisdiction}"…`
      : 'Select a jurisdiction…'
    return (
      <option value="" disabled>
        {jurisdiction === ALL_FRAMEWORKS_LABEL
          ? 'Select a framework…'
          : jurisdictionLabel}
      </option>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
      }}
    >
      <SearchableSelect
        label="Filter by State (optional)"
        options={jurisdictions}
        value={jurisdiction}
        onChange={setJurisdiction}
        placeholder="Type state abbreviation (e.g. FL, CA).."
      />

      <div>
        <label
          htmlFor="framework-select"
          style={{
            display: 'block',
            marginBottom: 4,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Select State - Subject Database
        </label>

        <select
          id="framework-select"
          disabled={!frameworksInJurisdiction?.length}
          value={value?.id ?? ''}
          onChange={(e) => handleFrameworkChange(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 10px',
            borderRadius: 6,
            border: '1px solid #ccc',
            fontSize: 14,
            backgroundColor:
              !jurisdiction || frameworksInJurisdiction?.length === 0
                ? '#f5f5f5'
                : '#fff',
          }}
        >
          <OptionLabel />
          {frameworksInJurisdiction?.map((fw) => (
            <option key={fw.id} value={fw.id}>
              {fw.name} • {fw.jurisdiction} • v{fw.version_label}
            </option>
          ))}
        </select>

        {jurisdiction && !frameworksInJurisdiction?.length && (
          <p style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
            No frameworks found for this jurisdiction.
          </p>
        )}
      </div>
    </div>
  )
}

type SearchableSelectProps = {
  label?: string
  placeholder?: string
  options: string[]
  value: string | null
  onChange: (value: string | null) => void
}

function SearchableSelect({
  label,
  placeholder = 'Type to search…',
  options,
  value,
  onChange,
}: SearchableSelectProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((opt) => opt.toLowerCase().includes(q))
  }, [options, query])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (opt: string) => {
    onChange(opt)
    setQuery('')
    setIsOpen(false)
  }

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true)
      return
    }

    if (!filteredOptions.length) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((prev) =>
        prev + 1 < filteredOptions.length ? prev + 1 : prev,
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const opt = filteredOptions[highlightIndex]
      if (opt) handleSelect(opt)
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {label && (
        <label
          style={{
            display: 'block',
            marginBottom: 4,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {label}
        </label>
      )}

      <div
        style={{
          border: '1px solid #ccc',
          borderRadius: 6,
          padding: 8,
          cursor: 'text',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          backgroundColor: 'white',
        }}
        onClick={() => setIsOpen(true)}
      >
        <div style={{ fontSize: 13, color: '#555' }}>
          {value ?? (
            <span style={{ color: '#999' }}>No jurisdiction selected</span>
          )}
        </div>
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
            setHighlightIndex(0)
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{
            border: 'none',
            outline: 'none',
            fontSize: 14,
            padding: '4px 0',
          }}
        />
      </div>

      {isOpen && (
        <ul
          style={{
            position: 'absolute',
            zIndex: 10,
            marginTop: 4,
            maxHeight: 260,
            overflowY: 'auto',
            width: '100%',
            listStyle: 'none',
            padding: 0,
            borderRadius: 6,
            border: '1px solid #ddd',
            backgroundColor: '#fff',
            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
          }}
        >
          {filteredOptions.length === 0 && (
            <li
              style={{
                padding: '8px 10px',
                fontSize: 13,
                color: '#777',
              }}
            >
              No results
            </li>
          )}

          {filteredOptions.map((opt, index) => {
            const isHighlighted = index === highlightIndex
            return (
              <li
                key={opt}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelect(opt)
                }}
                onMouseEnter={() => setHighlightIndex(index)}
                style={{
                  padding: '8px 10px',
                  cursor: 'pointer',
                  fontSize: 13,
                  backgroundColor: isHighlighted ? '#f0f4ff' : '#fff',
                }}
              >
                {opt}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
